import React, { useState, useMemo, useCallback } from 'react'
import { Row, Col } from 'react-bootstrap'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import StatusBadge, {
  StatusTypes,
} from '@components/common/StatusBadge/StatusBadge.component'
import Button from '@components/common/Button/Button.component'
import updatesData from '../../../assets/mockdata/PatientTracker/updatesData.json'
import './PatientStatusTracker.css'

type Column = {
  field: string
  headerText: string
  textAlign: 'Left' | 'Right' | 'Center'
  width: number
  template?: (props: any) => JSX.Element
}

type UpdatesItem = {
  id: number
  isSelected: boolean
  isStarred: boolean
  content: string
  time: string
  status: StatusTypes
  date?: string
}

const PatientUpdates: React.FC = (): JSX.Element => {
  const [data, setData] = useState<UpdatesItem[]>(
    updatesData.map((item) => ({
      ...item,
      status: item.status as StatusTypes,
    })),
  )

  const [archivedData, setArchivedData] = useState<UpdatesItem[]>([])
  const [view, setView] = useState<'inbox' | 'archive'>('inbox')
  const [selectedStatus, setSelectedStatus] = useState<StatusTypes | null>(null)

  const filteredData = useMemo(() => {
    if (!selectedStatus) return data
    return data.filter((item) => item.status === selectedStatus)
  }, [data, selectedStatus])

  const filteredTodayData = useMemo(
    () =>
      filteredData.filter((item) => !item.date || item.date !== 'Yesterday'),
    [filteredData],
  )

  const filteredYesterdayData = useMemo(
    () => filteredData.filter((item) => item.date === 'Yesterday'),
    [filteredData],
  )

  const isAnySelected = filteredData.some((item) => item.isSelected)

  const markAsButtonLabel = isAnySelected ? 'Mark as Read' : 'Mark as'

  const handleCheckboxChange = useCallback((id: number): void => {
    setData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, isSelected: !item.isSelected } : item,
      ),
    )
  }, [])

  const handleSendToArchive = (): void => {
    const selectedItems = data.filter((item) => item.isSelected)
    setArchivedData((prevArchivedData) => [
      ...prevArchivedData,
      ...selectedItems,
    ])
    setData((prevData) => prevData.filter((item) => !item.isSelected))
  }

  const columns: Column[] = useMemo(
    () => [
      {
        field: 'actions',
        headerText: '',
        textAlign: 'Left',
        width: 100,
        template: (props) => (
          <div className="patient-updates-row">
            <div className="patient-updates-badge-container">
              <input
                type="checkbox"
                checked={props.isSelected}
                onChange={() => handleCheckboxChange(props.id)}
                className="patient-updates-checkbox-input"
              />
              <StatusBadge status={props.status} />
            </div>
            <span className="patient-updates-message">{props.content}</span>
          </div>
        ),
      },
      {
        field: 'time',
        headerText: 'Time',
        textAlign: 'Right',
        width: 100,
      },
    ],
    [handleCheckboxChange],
  )

  return (
    <>
      <Row className="gx-3">
        <Col md={3} style={{ width: '20%' }}>
          <div className="message-navigation">
            <div className="search-box">
              <i className="ti ti-search"></i>
              <input
                type="text"
                placeholder="Search"
                className="search-input"
              />
            </div>
            <div className="navigation-items">
              <div
                onClick={() => {
                  setView('inbox')
                  setSelectedStatus(null)
                }}
                className={`cursor-pointer ${view === 'inbox' ? 'active' : ''}`}
              >
                <i className="ti ti-inbox"></i> Inbox
              </div>
              <div
                onClick={() => {
                  setView('archive')
                  setSelectedStatus(null)
                }}
                className={`cursor-pointer ${
                  view === 'archive' ? 'active' : ''
                }`}
              >
                <i className="ti ti-archive"></i> Archive
              </div>
            </div>
            <div className="label-section">
              <div className="label-title">Tags</div>
              <div className="label-items">
                <div
                  onClick={() => {
                    setSelectedStatus(StatusTypes.Marketing)
                    setView('inbox')
                  }}
                  className={`cursor-pointer ${
                    selectedStatus === StatusTypes.Marketing ? 'active' : ''
                  }`}
                >
                  <i className="ti ti-tag-filled marketing-icon"></i> Marketing
                </div>
                <div
                  onClick={() => {
                    setSelectedStatus(StatusTypes.Appointments)
                    setView('inbox')
                  }}
                  className={`cursor-pointer ${
                    selectedStatus === StatusTypes.Appointments ? 'active' : ''
                  }`}
                >
                  <i className="ti ti-tag-filled appointments-icon"></i>{' '}
                  Appointments
                </div>
                <div
                  onClick={() => {
                    setSelectedStatus(StatusTypes.Records)
                    setView('inbox')
                  }}
                  className={`cursor-pointer ${
                    selectedStatus === StatusTypes.Records ? 'active' : ''
                  }`}
                >
                  <i className="ti ti-tag-filled records-icon"></i> Records
                </div>
                <div
                  onClick={() => {
                    setSelectedStatus(StatusTypes.Payments)
                    setView('inbox')
                  }}
                  className={`cursor-pointer ${
                    selectedStatus === StatusTypes.Payments ? 'active' : ''
                  }`}
                >
                  <i className="ti ti-tag-filled payments-icon"></i> Payments
                </div>
                <div
                  onClick={() => {
                    setSelectedStatus(StatusTypes.Alerts)
                    setView('inbox')
                  }}
                  className={`cursor-pointer ${
                    selectedStatus === StatusTypes.Alerts ? 'active' : ''
                  }`}
                >
                  <i className="ti ti-tag-filled alerts-icon"></i> Alerts
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col>
          {view === 'inbox' ? (
            <div className="message-main hide-grid-header">
              <div className="inbox-header d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center">
                  <i className="ti ti-reload"></i>
                  <span className="ps-2">Refresh</span>
                </div>
                <div className="d-flex">
                  <Button
                    size="md"
                    className="button-secondary me-2"
                    disabled={!isAnySelected}
                    onClick={handleSendToArchive}
                  >
                    Send to Archive
                  </Button>
                  <Button
                    size="md"
                    className="button-primary"
                    disabled={!isAnySelected}
                  >
                    {markAsButtonLabel}
                  </Button>
                </div>
              </div>

              <div className="message-main-title">Today</div>
              <DataGridComponent data={filteredTodayData} columns={columns} />

              <div className="message-main-title">Yesterday</div>
              <DataGridComponent
                data={filteredYesterdayData}
                columns={columns}
              />
            </div>
          ) : (
            <div className="message-main hide-grid-header">
              <div className="message-main-title">Archived</div>
              <DataGridComponent data={archivedData} columns={columns} />
            </div>
          )}
        </Col>
      </Row>
    </>
  )
}

export default PatientUpdates
